import { useNavigate } from 'react-router-dom';
import {
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';
import {
    Bookmark,
    ShoppingBag,
    Trash2,
} from 'lucide-react';
import { toast } from 'sonner';

import {
    extractCart,
    fetchActiveCart,
} from './Cart/api';
import { CartEmptyState } from './Cart/CartEmptyState';
import { CartItemCard } from './Cart/CartItemCard';
import { CartOrderSummary } from './Cart/CartOrderSummary';
import { getSupplierName } from './Cart/formatters';
import { useCartMutations } from './Cart/useCartMutations';
import { saveCartAsOrder } from './SavedOrders/api';

import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import {
    Card,
    CardContent,
} from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { useRbac } from '@/hooks/useRbac';
import { PERMISSIONS } from '@/lib/rbac';
import {
    extractApiError,
    extractArray,
} from '@/lib/shared-utils';
import {
    fetchAllConnectedUserProducts,
    fetchConnections,
} from '@/pages/RelatedParties/api';
import {
    getUnitOptions,
    type UnitOption,
} from '@/pages/RelatedParties/components/cart-utils';
import type {
    ConnectionUser,
    PartyProduct,
} from '@/pages/RelatedParties/types';

function buildUnitOptionsMap(
    products: PartyProduct[] | undefined,
): Map<string, UnitOption[]> {
    const map = new Map<string, UnitOption[]>();
    if (!products) return map;
    products.forEach((product) => {
        map.set(product.slug, getUnitOptions(product));
    });
    return map;
}

export default function Cart() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { can } = useRbac();
    const canPlaceOrder = can(PERMISSIONS.orderCreate);
    const canSaveOrder = can(PERMISSIONS.orderSave);
    const canViewCart = can(PERMISSIONS.cartView);
    const canManageCart = can(PERMISSIONS.cartManage);

    const saveOrderMutation = useMutation({
        mutationFn: saveCartAsOrder,
        onSuccess: async (response) => {
            if (!response.ok) {
                toast.error(extractApiError(response.data, 'Unable to save this cart.'));
                return;
            }
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ['cart'] }),
                queryClient.invalidateQueries({ queryKey: ['saved-orders'] }),
            ]);
            toast.success('Cart saved. You can order it later from Saved Orders.');
            navigate('/orders/saved');
        },
        onError: (mutationError) => {
            toast.error(
                mutationError instanceof Error
                    ? mutationError.message
                    : 'Unable to save this cart.',
            );
        },
    });

    const {
        data: cartResponse,
        isLoading,
        isError,
        error,
    } = useQuery({
        queryKey: ['cart'],
        queryFn: fetchActiveCart,
    });

    const cart = cartResponse?.ok
        ? extractCart(cartResponse.data)
        : null;
    const items = cart?.items ?? [];

    const { data: connectionsRes } = useQuery({
        queryKey: ['connections'],
        queryFn: fetchConnections,
        enabled: !!cart?.supplier?.id,
    });
    const connectionsLoaded = !!connectionsRes;
    const isSupplierConnected = !connectionsLoaded || !cart?.supplier?.id
        ? true
        : extractArray<ConnectionUser>(connectionsRes?.data)
            .some((c) => c.id === cart.supplier.id);

    const cartSupplierId = cart?.supplier?.id;
    const { data: supplierProducts } = useQuery({
        queryKey: ['supplier-product-units', cartSupplierId],
        queryFn: () => fetchAllConnectedUserProducts(cartSupplierId!),
        enabled: !!cartSupplierId && canManageCart,
    });
    const unitOptionsBySlug = buildUnitOptionsMap(supplierProducts);

    const {
        pendingProductSlug,
        stockErrors,
        editingQuantities,
        handleEditQuantity,
        clearCartMutation,
        checkoutMutation,
        increaseQuantity,
        decreaseQuantity,
        commitQuantity,
        changeUnit,
        removeItem,
    } = useCartMutations(cartSupplierId);
    const stockErrorBySlug = new Map(
        stockErrors.map((stockError) => [stockError.product_slug, stockError]),
    );

    const handleNavigateToProduct = (supplierId: number | undefined, slug: string) => {
        if (!supplierId) return;
        navigate(`/related-parties/party/${supplierId}/products/${slug}`);
    };

    if (!canViewCart) {
        return (
            <div>
                <Card className="border-0 shadow-sm">
                    <CardContent className="py-10 text-sm text-muted-foreground">
                        You don&apos;t have permission to view the cart.
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div>
                <Card className="border-0 shadow-sm">
                    <CardContent className="flex items-center justify-center py-20">
                        <Spinner className="h-6 w-6 text-muted-foreground" />
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (isError) {
        return (
            <div>
                <Card className="border-0 shadow-sm">
                    <CardContent className="py-10 text-sm text-destructive">
                        Error loading cart:
                        {' '}
                        {error instanceof Error ? error.message : 'Unknown error'}
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (items.length === 0) {
        return <CartEmptyState onBrowse={() => navigate('/related-parties')} />;
    }

    const supplierName = getSupplierName(cart?.supplier);

    return (
        <div className="space-y-6">
            <PageHeader
                icon={<ShoppingBag className="w-5 h-5 text-primary" />}
                title="Shopping Cart"
                description={
                    `${items.length} ${items.length === 1 ? 'item' : 'items'}`
                    + ` from ${supplierName}`
                }
                actions={(
                    <div className="flex items-center gap-2">
                        {canSaveOrder && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => saveOrderMutation.mutate()}
                                disabled={saveOrderMutation.isPending}
                            >
                                {saveOrderMutation.isPending
                                    ? <Spinner className="mr-1.5 h-4 w-4" />
                                    : <Bookmark className="mr-1.5 h-4 w-4" />}
                                Save for later
                            </Button>
                        )}
                        {canManageCart && (
                            <Button
                                variant="outline"
                                size="sm"
                                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                                onClick={() => clearCartMutation.mutate()}
                                disabled={clearCartMutation.isPending}
                            >
                                {clearCartMutation.isPending
                                    ? <Spinner className="mr-1.5 h-4 w-4" />
                                    : <Trash2 className="mr-1.5 h-4 w-4" />}
                                Clear Cart
                            </Button>
                        )}
                    </div>
                )}
            />

            <div className="rounded-2xl border border-warning/30 bg-warning/10 p-4 text-sm text-warning">
                One cart can only contain items from a single
                related party. To shop from another party, save
                this cart for later or clear it first.
            </div>

            <div className="grid gap-6 lg:grid-cols-3">
                <div className="space-y-3 lg:col-span-2">
                    {items.map((item) => (
                        <CartItemCard
                            key={item.id}
                            item={item}
                            supplierName={supplierName}
                            supplierId={cart?.supplier?.id}
                            isPending={pendingProductSlug === item.product_slug}
                            editingQuantity={editingQuantities[item.product_slug]}
                            stockError={stockErrorBySlug.get(item.product_slug)}
                            onNavigateToProduct={handleNavigateToProduct}
                            onEditQuantity={handleEditQuantity}
                            onCommitQuantity={commitQuantity}
                            onIncrease={increaseQuantity}
                            onDecrease={decreaseQuantity}
                            onRemove={removeItem}
                            onChangeUnit={changeUnit}
                            unitOptions={unitOptionsBySlug.get(item.product_slug)}
                            canManage={canManageCart}
                        />
                    ))}
                </div>

                <CartOrderSummary
                    cart={cart}
                    items={items}
                    connectionsLoaded={connectionsLoaded}
                    isSupplierConnected={isSupplierConnected}
                    isCheckoutPending={checkoutMutation.isPending}
                    canPlaceOrder={canPlaceOrder}
                    onPlaceOrder={() => checkoutMutation.mutate()}
                    onContinueShopping={() => navigate(
                        cart?.supplier?.id
                            ? `/related-parties/party/${cart.supplier.id}/products`
                            : '/related-parties',
                    )}
                />
            </div>
        </div>
    );
}
